import { IconFa } from '@/components/wm';
import { cn } from '@/utils';
import React, { useEffect, useState } from 'react';
import { datesInterval } from '../utils';

// How this widget renders each of the shared PALETTE_OPTIONS. Private on purpose:
// the palette names are the shared vocabulary, the classes are not.
const paletteVariants = {
    primary: 'tw-bg-primary tw-text-secondary',
    secondary: 'tw-bg-secondary tw-text-gray-200',
    simple: 'tw-bg-white tw-border tw-border-solid tw-border-border tw-text-secondary'
};

const iconVariants = {
    primary: 'tw-text-secondary',
    secondary: 'tw-text-gray-200',
    simple: 'tw-text-secondary'
};

// `sample`, when given, stands in for the indicator and its value: the type picker
// shows each widget with made-up values.
export default function ValueCardWidget({ sample, widget }) {
    const
        isSmallWidget = widget.graphical.size.h <= 3 || widget.graphical.size.w <= 2,
        palette = widget.graphical.palette || 'primary';

    const
        [displayValue, setDisplayValue] = useState(null),
        [fontSize, setFontSize] = useState(1),
        [value, setValue] = useState(null),
        [timestamp, setTimestamp] = useState(null);

    const
        model = WiseMetering.indicators.get(widget.data.indicatorId),
        ratio = widget.data.period === 'last_value' ? 900 : 0;

    const
        icon = WiseMetering.icons.getIconFromKind(model?.get('kind_slug')) || 'info',
        unit = sample?.unit ?? (model?.get('unit') || '');

    useEffect(() => {
        // More cases can be added here in the future, returning before the fetch
        // CASE A: Indicators, API realtime fetch

        if (sample) {
            setValue(sample.value);
            setDisplayValue(formatValue(sample.value, unit, widget.data.decimals));
            return;
        }

        if (!model) {
            setValue('--');
            return;
        }

        const fetchValue = () => {
            if (widget.data.period === 'last_value') {
                model.fetchIndicatorData('/last_value', {}).then(response => {
                    const scale = widget.data.unitScale >= 0 ? widget.data.unitScale : undefined;
                    if (response) {
                        const val = response.v;
                        const ts = response.t;

                        if (val !== null && val !== undefined) {
                            setValue(val);
                            setTimestamp(ts);
                            setDisplayValue(formatValue(val, unit, widget.data.decimals, scale));
                            return;
                        }
                    }
                    setDisplayValue('--');
                    setValue(null);
                    setTimestamp(null);
                }).catch(() => {
                    setDisplayValue('--');
                    setValue(null);
                    setTimestamp(null);
                });
                return;
            }

            const period = datesInterval(widget.data.period, model.timezone());

            const params = {
                from: period.from,
                to: period.to,
                rate: ratio
            };

            // Aggregation is resolved by the backend when ratio is 0 (most current cases), if not, use the last value
            model.getRealtimeData(params).then(response => {
                const scale = widget.data.unitScale >= 0 ? widget.data.unitScale : undefined;

                if (Array.isArray(response.data) && response.data.length > 0) {
                    const cleaned = response.data.filter(o => o.v !== null);

                    if (cleaned.length === 0) {
                        setDisplayValue('--');
                        return;
                    }

                    const latestDataPoint = cleaned[cleaned.length - 1].v;

                    setValue(latestDataPoint);

                    setDisplayValue(formatValue(latestDataPoint, unit, widget.data.decimals, scale));
                } else if (response?.data !== undefined && response?.data !== null) {
                    setValue(response?.data);

                    setDisplayValue(formatValue(response?.data, unit, widget.data.decimals, scale));
                } else {
                    setDisplayValue('--');
                }
            })
                .catch(() => setDisplayValue('--'));
        };

        fetchValue();

        // Then refresh every minute
        const interval = setInterval(fetchValue, 60000);

        return () => clearInterval(interval);
    }, [widget.data.indicatorId, widget.data.period]);

    useEffect(() => {
        const scale = widget.data.unitScale >= 0 ? widget.data.unitScale : undefined;

        setDisplayValue(formatValue(value, unit, widget.data.decimals, scale));
    }, [widget.data.decimals, widget.data.unitScale]);

    useEffect(() => {
        const widgetElement = document.getElementById(widget.id);
        const characterCount = Math.max(displayValue ? displayValue.length : 9, 5); // 9 is for the loading... and 5 is to avoid too large fonts for small values

        let baseDimension = widgetElement?.offsetWidth || 100;

        if (widget.graphical.size.h <= 3 || widget.graphical.size.w <= 2) {
            const widgetHeightPx = widgetElement?.offsetHeight || 100;
            baseDimension = Math.min(baseDimension, widgetHeightPx);
        }

        const maxFontSize = 80; // max px
        const minFontSize = 12; // min px

        const multiplier = isSmallWidget ? 1.1 : 1.35;

        const calculatedFontSize = Math.min(
            maxFontSize,
            Math.max(minFontSize, (baseDimension / characterCount) * multiplier)
        );

        setFontSize(calculatedFontSize);
    }, [widget.graphical.size.w, widget.graphical.size.h, displayValue]);

    return (
        <div
            className={cn(
                paletteVariants[palette],
                'tw-flex tw-flex-col tw-gap-1 tw-rounded-sm tw-px-4 tw-pt-4',
                'tw-min-h-full tw-min-w-full tw-flex tw-flex-col tw-justify-between tw-overflow-hidden tw-max-h-full'
            )}
            id={widget.id}
        >
            {/* Header (top) */}
            <div>
                <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-text-base tw-font-medium tw-leading-none">{widget.title}</div>
                    <IconFa color={iconVariants[palette]} icon={icon} size="medium"/>
                </div>
                <div className="tw-text-md tw-mt-0">
                    {widget.subtitle || ''}
                </div>
            </div>
            {/* Value + Legend (bottom, right-aligned) */}
            <div className="tw-flex tw-flex-col tw-items-end tw-pb-2">
                <div
                    className="tw-font-bold tw-text-right"
                    id="value"
                    style={{ fontSize: `${fontSize}px` }}
                >
                    {!displayValue ? 'Loading…' : displayValue}
                </div>
                <div className={cn('tw-font-medium tw-text-right tw-mt-[-6px]', isSmallWidget ? 'tw-text-sm' : 'tw-text-base')}>
                    {widget.legend || ''}
                </div>
                {widget.data.period === 'last_value' && timestamp && (
                    <div className="tw-text-[11px] tw-text-right tw-opacity-70 tw-mt-[-4px]">
                        {moment(typeof timestamp === 'number' ? timestamp * 1000 : timestamp).tz(model?.timezone() || moment.tz.guess()).format('YYYY/MM/DD HH:mm')}
                    </div>
                )}
            </div>
        </div>
    );
}
